import { useCallback, useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "./redux";
import { applyDeviceFetchResult } from "../redux/Clinic/clinic.slice";
import { clinicController } from "../AppController";
import { ASSIGNED_DEVICE_REFRESH_INTERVAL_MS } from "../constants/restDataRefresh";
import { useDocumentVisible } from "./useDocumentVisible";
import { reportDeviceConnectivity } from "../sentry";
import { traceAssignedDeviceFetch } from "../util/deviceTrace";

/**
 * Periodically refetches assigned device (and connection status) from REST.
 * The timer is created/destroyed based on document visibility — when the app
 * goes to the background no timer exists at all (no wake-ups, no battery drain).
 * On foreground: immediate fetch + restart interval.
 */
export function useAssignedDevicePolling(): void {
  const dispatch = useAppDispatch();
  const visible = useDocumentVisible();
  const { patientId, clinicId, canPoll } = useAppSelector((state) => ({
    patientId: state.auth.user?.patient_id,
    clinicId: state.clinic.userClinic?.id,
    canPoll:
      !!state.clinic.clinicList?.length &&
      !!state.auth.user?.email &&
      !state.clinic.redirectToUserInfo &&
      // Pause while the user is in the device setup flow — Wi-Fi switching
      // fires repeated "online" events that would otherwise trigger a burst
      // of REST calls every time the phone reconnects to the internet.
      !state.clinic.isDeviceSetupActive,
  }));

  const refresh = useCallback(async () => {
    if (!patientId || patientId <= 0 || !clinicId) return;
    try {
      const { success, data } = await clinicController.fetchAssignedDevice({
        clinic_id: clinicId,
        patient_id: patientId,
      });
      // A soft failure resolves with success:false and no data — never let it
      // overwrite a known-good device with null (that's the "false disconnect"
      // bug). The reducer keeps the previous device and debounces transient
      // offline readings.
      if (!success) {
        reportDeviceConnectivity("fetch_failed_preserved", { source: "poll" });
      }
      // Trace any successful read that reported "no device" — the smoking gun
      // for the intermittent unpair reports.
      traceAssignedDeviceFetch({
        success,
        data,
        source: "poll",
        clinicId,
        patientId,
      });
      dispatch(applyDeviceFetchResult({ success, device: data ?? null }));
    } catch {
      // Defensive: the controller normally returns success:false rather than
      // throwing, but if it does throw we still must not clobber good state.
      reportDeviceConnectivity("fetch_threw_preserved", { source: "poll" });
    }
  }, [dispatch, patientId, clinicId]);

  const intervalRef = useRef<number | null>(null);

  useEffect(() => {
    if (!canPoll || !patientId || patientId <= 0 || !clinicId || !visible) {
      if (intervalRef.current !== null) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Foreground: immediate fetch then start interval.
    void refresh();
    intervalRef.current = window.setInterval(
      () => void refresh(),
      ASSIGNED_DEVICE_REFRESH_INTERVAL_MS
    );

    const onOnline = () => void refresh();
    window.addEventListener("online", onOnline);

    return () => {
      if (intervalRef.current !== null) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      window.removeEventListener("online", onOnline);
    };
  }, [canPoll, patientId, clinicId, refresh, visible]);
}
